import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const GoogleShopping = () => {
  useSEO({
    title: 'Google Shopping Integration | Ommni - Optimize Product Feeds',
    description: 'Integrate Ommni with Google Shopping. Optimize product feeds, improve rankings, ensure data quality, and sync inventory in real-time. Maximize visibility on Google Shopping.',
    keywords: 'google shopping integration, product feed optimization, google merchant center, shopping ads, product listings',
    canonical: 'https://ommni.io/integrations/google-shopping',
  });

  const benefits = [
    "Assessment of product data quality",
    "Enhanced uniformity in product listings and brand representation",
    "Improved ranking in Google Shopping searches",
    "Minimized daily maintenance for each channel",
    "Direct access to the official Google Merchant guidelines",
    "Capability to dispatch data to both Google Shopping and Manufacturer accounts"
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="pt-32 px-4 pb-24">
        <div className="container mx-auto max-w-4xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Google Shopping Integration
            </h1>
            <p className="text-xl text-muted-foreground">
              Elevate your presence on Google Shopping with Ommni's powerful PIM connector
            </p>
          </div>

          {/* Main Content */}
          <div className="space-y-12">
            {/* Section 1 */}
            <Card className="p-8 gradient-card">
              <h2 className="text-3xl font-bold mb-6">
                How Ommni's PIM Connector Elevates Your Google Shopping Presence
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Google Shopping stands as a premier online marketplace, offering a vast stage for manufacturers and retailers to showcase and vend their products. Nonetheless, due to the marketplace's magnitude and Google's specific SEO stipulations, many brands find it challenging to distinguish their offerings.
                </p>
                <p>
                  By providing complete, current, and SEO-tailored product data, businesses can enhance their products' visibility and secure a more prominent position in the digital landscape. Ommni's PIM streamlines the process of centralizing product data, assessing its quality, and implementing bulk modifications. With our integrated connector for Google Shopping and Merchant, your products can hit the market more swiftly than through any other PIM solution.
                </p>
                <p>
                  Both manufacturers and retailers have the capability to centralize and regulate their product data for Google Shopping and Google Manufacturer with Ommni's PIM. Our platform empowers you to tailor your data for a multi-channel strategy, choosing precisely the products or catalogs to exhibit on your Google Shopping feed.
                </p>
                <p>
                  As an official Google partner, Ommni has crafted a connector that caters to the distinct demands of Google Shopping sellers, eliminating the need for intricate setup procedures.
                </p>
              </div>
            </Card>

            {/* Section 2 */}
            <Card className="p-8 gradient-card">
              <h2 className="text-3xl font-bold mb-6">
                What capabilities does Ommni's Google Shopping Connector offer?
              </h2>
              <div className="space-y-6">
                <p className="text-muted-foreground">
                  Manually managing an extensive array of products across various marketplaces can be cumbersome and complex. Manual data uploads and updates can substantially burden your product marketing teams, particularly when your multi-channel strategy involves displaying differentiated products on each sales channel.
                </p>
                <p className="text-muted-foreground">
                  Consolidating all your product information within Ommni's PIM system facilitates the management of your catalog data across all channels. Our Google Shopping connector allows you to select specific products or catalogs for connection. Once designated, product details are automatically in sync between the PIM and Google Shopping through API or XML, also updating your Google Ads correspondingly.
                </p>
                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    Benefits of using Ommni's Google Shopping & Merchant connector:
                  </h3>
                  <ul className="space-y-3">
                    {benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>

            {/* CTA Section */}
            <div className="text-center pt-8">
              <Button variant="cta" size="lg" asChild>
                <Link to="/contact">
                  Get Started with Google Shopping Integration <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Breadcrumbs />
      <Footer />
    </div>
  );
};

export default GoogleShopping;
